import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { TENANT } from "@types/tenant";
import { CONTRACT } from "@types/contract";
import {
  getTenantData,
  putTenantData,
} from "@services/API/Requests/TenantRequests";
import { getAppData } from "@services/API/Requests/AppRequests";
import {
  getTenantContractData,
  putContractData,
} from "@services/API/Requests/ContractRequests";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { BlankTenantData } from "@templates/BlankTenantData";
import { error_message } from "@constants/Errors";
import { APP_NAMES, CONTRACT_STATUS } from "@constants/Enums";
import { handleResponse } from "@services/API/StatusCodes";
import { validateDates } from "@components/Forms/common/utilities/tenant/ValidateDates";
import { validateUnitPrice } from "@components/Forms/common/utilities/tenant/ValidateUnitPrice";

export const useViewTenantForm = (tenantCode?: string) => {
  const useHazumApi = import.meta.env.VITE_USE_HAZUM_API === "true";
  const isAdminLoggedIn: boolean = useSelector(
    (state: any) => state.auth.isAdmin,
  );
  const navigate = useNavigate();

  const id: string | null = tenantCode || localStorage.getItem("realm");

  const [tenantData, setTenantData] = useState<TENANT | undefined>(
    BlankTenantData,
  );
  const [contractData, setContractData] = useState<CONTRACT[]>([]);
  const [appData, setAppData] = useState<any[]>([]);

  const [hasYobelyLicense, setHasYobelyLicense] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [secondSetLoading, setSecondSetLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);

  const [dateError, setDateError] = useState<boolean>(false);
  const [unitPriceError, setUnitPriceError] = useState<boolean>(false);

  const [errors, setErrors] = useState<any>({});

  const errorToast = (msg: string) => toast.error(msg);
  const successToast = (msg: string) => toast.success(msg);

  const updateTenantData = (key: keyof TENANT, value: string | boolean) => {
    setTenantData((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateContractData = (
    app_id: number,
    key: keyof CONTRACT,
    value: any,
  ) => {
    setContractData((prev) =>
      prev.map((contract) =>
        contract.app_id === app_id ? { ...contract, [key]: value } : contract,
      ),
    );
  };

  const updateError = (field: string, value: boolean) => {
    setErrors((prev) => ({ ...prev, [field]: value }));
  };

  const verifyFields = () => {
    return !Object.values(errors).some((error) => error === true);
  };

  const updateYobelyLicenseStatus = (contracts: CONTRACT[]) => {
    const hasYobely = contracts.some((contract) => {
      const app = appData.find((a) => a.id === contract.app_id);
      return (
        app?.name === APP_NAMES.YOBELY &&
        contract.status === CONTRACT_STATUS.ACTIVE
      );
    });
    setHasYobelyLicense(hasYobely);
  };

  const fetchTenantData = async () => {
    try {
      await getTenantData(setTenantData, setLoading, setError, id);
    } catch (error) {
      setError(error);
    }
  };

  const fetchLicenseData = async () => {
    try {
      await getTenantContractData(
        setContractData,
        setSecondSetLoading,
        setError,
        id,
      );
    } catch (error) {
      setError(error.message || "");
    }
  };

  const fetchAppData = async () => {
    await getAppData(setAppData, setError, setSecondSetLoading);
  };

  useEffect(() => {
    if (id) {
      fetchTenantData();
      fetchLicenseData();
      fetchAppData();
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    updateYobelyLicenseStatus(contractData);
  }, [contractData, appData]);

  const onSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    isAdminLoggedIn: boolean,
  ) => {
    event.preventDefault();
    setError("");

    if (!verifyFields()) return errorToast(error_message.generic.bad_field);
    if (!validateDates(errorToast, setError, setDateError, contractData))
      return;
    if (
      isAdminLoggedIn &&
      !validateUnitPrice(errorToast, setUnitPriceError, contractData)
    )
      return;

    try {
      const response = await putTenantData(tenantData, setLoading, setError);

      if (response.status === 200) {
        if (isAdminLoggedIn) {
          const res = await putContractData(
            contractData,
            setLoading,
            setError,
            tenantCode,
          );
          if (res.status === 200) {
            successToast(handleResponse(res, "テナント").message);
            fetchLicenseData();
            fetchTenantData();
          } else {
            throw new Error(TranslateErrorMessage(res));
          }
        } else {
          successToast(response.message);
        }
      } else {
        throw new Error(TranslateErrorMessage(response.message));
      }
    } catch (err: any) {
      setError(err.message);
      errorToast(err.message);
    }
  };

  const handleKeycloakGroupsCheckboxChange = useCallback(
    (selectedGroup) => {
      const existingGroups = tenantData?.keycloak_groups?.tenant_groups || [];
      const groupExists = existingGroups.some(
        (g: any) => g.name === selectedGroup.full_path,
      );

      const updatedGroups = groupExists
        ? existingGroups.filter((g: any) => g.name !== selectedGroup.full_path)
        : [
            ...existingGroups,
            {
              id: selectedGroup.id || 0,
              name: selectedGroup.full_path,
              parent: selectedGroup.parent,
              full_path: selectedGroup.full_path,
            },
          ];

      updateTenantData("keycloak_groups", {
        ...tenantData.keycloak_groups,
        tenant_groups: updatedGroups,
      });
    },
    [tenantData],
  );

  const onCancel = () => {
    navigate(!isAdminLoggedIn ? "/home" : "/company/manage");
  };

  return {
    id,
    tenantData,
    contractData,
    appData,
    hasYobelyLicense,
    loading,
    error,
    warningModalOpen,
    errors,
    updateTenantData,
    setLoading,
    setContractData,
    updateContractData,
    handleKeycloakGroupsCheckboxChange,
    updateError,
    onSubmit,
    onCancel,
    handleOpenWarningModal: () => setWarningModalOpen(true),
    handleCloseWarningModal: () => setWarningModalOpen(false),
    useHazumApi,
    isAdminLoggedIn,
  };
};
